import React, { useState } from "react";
import DefaultFooter from "../components/default-footer";
import DefaultHeader from "../components/default-header";
import { useTranslation } from "react-i18next";
import { useSeo } from "../components/useSeo";

/* ─────────────────────────────────────────────
   COLOUR PALETTE (Brand Theme)
───────────────────────────────────────────── */
const C = {
  navy: "#294b5a",
  navyDark: "#11222C",
  navyLight: "#3a627a",
  gold: "#d3be76",
  goldLight: "#f4ebd0",
  goldDark: "#b09a52",
  cream: "#FAF8F5",
  white: "#ffffff",
  textDark: "#0f172a",
  textMid: "#475569",
  textLight: "#94a3b8",
  border: "rgba(41,75,90,0.12)",
};


const SchengenVisa = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Phase 3 note: this page covers the same topic as /visa-schengen (the
  // fuller page with plans/pricing/FAQ). Rather than compete as duplicate
  // content, canonical points to /visa-schengen as the primary page — see
  // the Phase 3 report section on duplicate Schengen routes for the full
  // picture (a 3rd route, /info-shengen, has the same treatment).
  useSeo({
    title: "Book a Schengen Visa Consultation | SilverJarz",
    description: "Book a Schengen visa consultation with SilverJarz — expert guidance and application support for residents of Oman.",
    canonical: `${origin}/visa-schengen`,
  });


  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const { t, i18n } = useTranslation();

  const COUNTRIES = [
    { flag: "🇩🇪", name: t("scCountryGermany") },
    { flag: "🇫🇷", name: t("scCountryFrance") },
    { flag: "🇮🇹", name: t("scCountryItaly") },
    { flag: "🇪🇸", name: t("scCountrySpain") },
    { flag: "🇳🇱", name: t("scCountryNetherlands") },
    { flag: "🇦🇹", name: t("scCountryAustria") },
    { flag: "🇨🇭", name: t("scCountrySwitzerland") },
    { flag: "🇧🇪", name: t("scCountryBelgium") },
    { flag: "🇵🇹", name: t("scCountryPortugal") },
    { flag: "🇬🇷", name: t("scCountryGreece") },
    { flag: "🇸🇪", name: t("scCountrySweden") },
    { flag: "🇩🇰", name: t("scCountryDenmark") },
  ];

  const STEPS = [
    {
      num: "01",
      title: t("scStep1Title"),
      desc: t("scStep1Desc"),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      ),
    },
    {
      num: "02",
      title: t("scStep2Title"),
      desc: t("scStep2Desc"),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <path d="M14 2v6h6"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      ),
    },
    {
      num: "03",
      title: t("scStep3Title"),
      desc: t("scStep3Desc"),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
    },
    {
      num: "04",
      title: t("scStep4Title"),
      desc: t("scStep4Desc"),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      ),
    },
  ];

  const DOCS = [
    t("scDoc1"),
    t("scDoc2"),
    t("scDoc3"),
    t("scDoc4"),
    t("scDoc5"),
    t("scDoc6"),
    t("scDoc7"),
    t("scDoc8"),
    t("scDoc9"),
    t("scDoc10"),
  ];

  const FAQS = [
    { q: t("scFaqQ1"), a: t("scFaqA1") },
    { q: t("scFaqQ2"), a: t("scFaqA2") },
    { q: t("scFaqQ3"), a: t("scFaqA3") },
    { q: t("scFaqQ4"), a: t("scFaqA4") },
  ];

  const TYPES = [
    {
      title: t("scType1Title"),
      code: t("scType1Code"),
      duration: t("scType1Duration"),
      features: [t("scType1F1"), t("scType1F2"), t("scType1F3"), t("scType1F4")],
      highlight: false,
    },
    {
      title: t("scType2Title"),
      code: t("scType2Code"),
      duration: t("scType2Duration"),
      features: [t("scType2F1"), t("scType2F2"), t("scType2F3"), t("scType2F4")],
      highlight: true,
    },
    {
      title: t("scType3Title"),
      code: t("scType3Code"),
      duration: t("scType3Duration"),
      features: [t("scType3F1"), t("scType3F2"), t("scType3F3"), t("scType3F4")],
      highlight: false,
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');

        .sv-page {
          font-family: 'Plus Jakarta Sans', sans-serif;
          color: ${C.textDark};
          background-color: ${C.white};
        }
        .sv-page * { box-sizing: border-box; }

        /* Typography */
        .sv-serif { font-family: 'Playfair Display', serif; }

        /* Buttons */
        .sv-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: linear-gradient(135deg, ${C.gold} 0%, ${C.goldDark} 100%);
          color: ${C.navyDark};
          font-weight: 700;
          font-size: 15px;
          padding: 14px 32px;
          border-radius: 8px;
          text-decoration: none;
          box-shadow: 0 10px 25px rgba(211, 190, 118, 0.3);
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }
        .sv-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 30px rgba(211, 190, 118, 0.45);
          color: ${C.navyDark};
        }

        .sv-btn-outline {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: transparent;
          color: ${C.white};
          font-weight: 600;
          font-size: 15px;
          padding: 13px 28px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.25);
          text-decoration: none;
          transition: all 0.3s ease;
        }
        .sv-btn-outline:hover {
          border-color: ${C.gold};
          background: rgba(211,190,118,0.1);
          color: ${C.goldLight};
        }

        /* Hero */
        .sv-hero {
          position: relative;
          background: radial-gradient(circle at 80% 20%, ${C.navyLight} 0%, ${C.navy} 60%, ${C.navyDark} 100%);
          padding: 100px 0 40px;
          overflow: hidden;
          color: ${C.white};
        }
        .sv-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }
        .sv-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(211, 190, 118, 0.12);
          border: 1px solid rgba(211, 190, 118, 0.3);
          color: ${C.goldLight};
          border-radius: 100px;
          padding: 6px 16px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 24px;
        }
        .sv-hero h1 {
          font-size: 24px;
          line-height: 1.15;
          margin-bottom: 24px;
          font-weight: 700;
        }
        .sv-hero h1 span {
          color: ${C.gold};
          font-style: italic;
        }
        .sv-hero p {
          font-size: 18px;
          line-height: 1.7;
          color: rgba(255,255,255,0.8);
          margin-bottom: 36px;
        }

        .sv-hero-card {
          position: relative;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 30px 60px rgba(0,0,0,0.3);
        }
        .sv-hero-card img {
          width: 100%;
          height: 380px;
          object-fit: cover;
          border-radius: 12px;
        }

        /* Stats Bar */
        .sv-stats {
          background: ${C.white};
          box-shadow: 0 10px 40px rgba(0,0,0,0.06);
          margin-top: -50px;
          position: relative;
          z-index: 10;
          border-radius: 16px;
          border: 1px solid ${C.border};
        }
        .sv-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          padding: 24px;
        }
        .sv-stat-item {
          text-align: center;
          padding: 16px;
          border-right: 1px solid ${C.border};
        }
        .sv-stat-item:last-child { border-right: none; }
        .sv-stat-num {
          font-size: 38px;
          font-weight: 800;
          color: ${C.navy};
          display: block;
          line-height: 1;
        }
        .sv-stat-label {
          font-size: 13px;
          font-weight: 600;
          color: ${C.textMid};
          margin-top: 8px;
          letter-spacing: 0.5px;
        }

        /* Section Styling */
        .sv-section { padding: 20px 0; }
        .sv-section-alt { background-color: ${C.cream}; }
        .sv-header-center {
          text-align: center;
          max-width: 640px;
          margin: 0 auto 50px;
        }
        .sv-eyebrow {
          color: ${C.goldDark};
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1.5px;
          margin-bottom: 12px;
          display: block;
        }
        .sv-title {
          font-size: 24px;
          font-weight: 700;
          color: ${C.navy};
          line-height: 1.25;
        }

        /* Country Grid */
        .sv-country-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 16px;
        }
        .sv-country-card {
          background: ${C.white};
          border: 1px solid ${C.border};
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          transition: all 0.3s ease;
        }
        .sv-country-card:hover {
          transform: translateY(-4px);
          border-color: ${C.gold};
          box-shadow: 0 12px 24px rgba(41, 75, 90, 0.08);
        }
        .sv-country-flag { font-size: 36px; display: block; margin-bottom: 8px; }
        .sv-country-name { font-size: 14px; font-weight: 600; color: ${C.textDark}; }

        /* Visa Types Grid */
        .sv-types-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          align-items: stretch;
        }
        .sv-type-card {
          background: ${C.white};
          border: 1px solid ${C.border};
          border-radius: 16px;
          padding: 40px 30px;
          display: flex;
          flex-direction: column;
          position: relative;
          transition: all 0.3s ease;
        }
        .sv-type-card.highlight {
          background: linear-gradient(180deg, ${C.navy} 0%, ${C.navyDark} 100%);
          color: ${C.white};
          border: none;
          box-shadow: 0 20px 40px rgba(17, 34, 44, 0.25);
          transform: scale(1.04);
        }
        .sv-type-badge {
          position: absolute;
          top: -14px;
          right: 24px;
          background: ${C.gold};
          color: ${C.navyDark};
          font-size: 11px;
          font-weight: 800;
          padding: 6px 14px;
          border-radius: 100px;
        }

        /* Process Steps */
        .sv-steps-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
        }
        .sv-step-card {
          background: ${C.white};
          border-radius: 16px;
          padding: 32px 24px;
          border: 1px solid ${C.border};
          position: relative;
        }
        .sv-step-number {
          font-size: 32px;
          font-weight: 800;
          color: ${C.gold};
          margin-bottom: 16px;
          display: block;
        }

        /* Document Check List */
        .sv-doc-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }
        .sv-doc-item {
          display: flex;
          align-items: center;
          gap: 14px;
          background: ${C.white};
          border: 1px solid ${C.border};
          padding: 16px 20px;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 500;
        }
        .sv-doc-check {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: rgba(211, 190, 118, 0.2);
          color: ${C.navy};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 800;
          flex-shrink: 0;
        }

        /* Accordion FAQ */
        .sv-faq-list {
          max-width: 800px;
          margin: 0 auto;
        }
        .sv-faq-item {
          background: ${C.white};
          border: 1px solid ${C.border};
          border-radius: 12px;
          margin-bottom: 16px;
          overflow: hidden;
        }
        .sv-faq-q {
          width: 100%;
          padding: 20px 24px;
          text-align: left;
          background: none;
          border: none;
          font-size: 17px;
          font-weight: 600;
          color: ${C.navy};
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
        }
        .sv-faq-a {
          padding: 0 24px 20px;
          font-size: 15px;
          line-height: 1.7;
          color: ${C.textMid};
        }

        /* Form CTA Section */
        .sv-cta-box {
          background: linear-gradient(135deg, ${C.navy} 0%, ${C.navyDark} 100%);
          border-radius: 24px;
          padding: 60px;
          color: ${C.white};
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          align-items: center;
        }
        .sv-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
          background: ${C.white};
          padding: 32px;
          border-radius: 16px;
          color: ${C.textDark};
        }
        .sv-input {
          width: 100%;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid ${C.border};
          font-size: 14px;
          outline: none;
        }
        .sv-input:focus {
          border-color: ${C.navy};
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .sv-hero-grid, .sv-types-grid, .sv-steps-grid, .sv-cta-box {
            grid-template-columns: 1fr;
          }
          .sv-stats-grid { grid-template-columns: repeat(2, 1fr); }
          .sv-stat-item { border-right: none; border-bottom: 1px solid ${C.border}; }
          .sv-doc-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 600px) {
          .sv-hero h1 { font-size: 24px; }
          .sv-stats-grid { grid-template-columns: 1fr; }
        }
      `}</style>

<div className="sv-page">
      <DefaultHeader />

      {/* ── HERO SECTION ─────────────────────── */}
      <section className="sv-hero">
        <div className="container">
          <div className="sv-hero-grid">
            <div>
              <div className="sv-badge">
                <span>🇴🇲</span> {t("scBadge")}
              </div>
              <h1 className="sv-serif">
                {t("scHeroTitle1")} <span>{t("scHeroTitleWord")}</span>
              </h1>
              <p>{t("scHeroSubtitle")}</p>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                <a href="#consultation" className="sv-btn-primary">
                  {t("scBookBtn")}
                </a>
                <a href="#process" className="sv-btn-outline">
                  {t("scOurProcessBtn")}
                </a>
              </div>
            </div>
            <div className="sv-hero-card">
              <img
                src="https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&w=800&q=80"
                alt="Europe travel destination"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── VISA TYPES ───────────────────────── */}
      <section className="sv-section">
        <div className="container">
          <div className="sv-header-center">
            <span className="sv-eyebrow">{t("scTypesEyebrow")}</span>
            <h2 className="sv-serif sv-title">{t("scTypesTitle")}</h2>
          </div>
          <div className="sv-types-grid">
            {TYPES.map((tItem, idx) => (
              <div key={idx} className={`sv-type-card ${tItem.highlight ? "highlight" : ""}`}>
                {tItem.highlight && <span className="sv-type-badge">{t("scMostPopular")}</span>}
                <span style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "1px", opacity: 0.8 }}>
                  {tItem.code}
                </span>
                <h3 className="sv-serif" style={{ fontSize: "24px", margin: "12px 0" }}>{tItem.title}</h3>
                <p style={{ fontSize: "14px", fontWeight: "600", color: tItem.highlight ? C.gold : C.navy, marginBottom: "24px" }}>
                  {tItem.duration}
                </p>
                <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: "12px", marginBottom: "32px" }}>
                  {tItem.features.map((f, i) => (
                    <div key={i} style={{ fontSize: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ color: tItem.highlight ? C.gold : C.goldDark }}>✓</span> {f}
                    </div>
                  ))}
                </div>
                <a href="#consultation" className={tItem.highlight ? "sv-btn-primary" : "sv-btn-outline"} style={!tItem.highlight ? { borderColor: C.navy, color: C.navy } : {}}>
                  {t("scApplyUnderCat")}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DESTINATIONS GRID ───────────────────── */}
      <section className="sv-section sv-section-alt">
        <div className="container">
          <div className="sv-header-center">
            <span className="sv-eyebrow">{t("scDestEyebrow")}</span>
            <h2 className="sv-serif sv-title">{t("scDestTitle")}</h2>
          </div>
          <div className="sv-country-grid">
            {COUNTRIES.map((c, i) => (
              <div key={i} className="sv-country-card">
                <span className="sv-country-flag">{c.flag}</span>
                <span className="sv-country-name">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS STEPS ────────────────────── */}
      <section className="sv-section" id="process">
        <div className="container">
          <div className="sv-header-center">
            <span className="sv-eyebrow">{t("scStepsEyebrow")}</span>
            <h2 className="sv-serif sv-title">{t("scStepsTitle")}</h2>
          </div>
          <div className="sv-steps-grid">
            {STEPS.map((s, i) => (
              <div key={i} className="sv-step-card">
                <span className="sv-step-number">{s.num}</span>
                <div style={{ marginBottom: "16px" }}>{s.icon}</div>
                <h4 style={{ fontSize: "18px", fontWeight: "700", color: C.navy, marginBottom: "8px" }}>{s.title}</h4>
                <p style={{ fontSize: "14px", color: C.textMid, lineHeight: "1.6" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DOCUMENTATION ──────────────────────── */}
      <section className="sv-section sv-section-alt">
        <div className="container">
          <div className="sv-header-center">
            <span className="sv-eyebrow">{t("scDocsEyebrow")}</span>
            <h2 className="sv-serif sv-title">{t("scDocsTitle")}</h2>
          </div>
          <div className="sv-doc-grid">
            {DOCS.map((doc, idx) => (
              <div key={idx} className="sv-doc-item">
                <span className="sv-doc-check">✓</span>
                <span>{doc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ACCORDION ──────────────────────── */}
      <section className="sv-section">
        <div className="container">
          <div className="sv-header-center">
            <span className="sv-eyebrow">{t("scFaqEyebrow")}</span>
            <h2 className="sv-serif sv-title">{t("scFaqTitle")}</h2>
          </div>
          <div className="sv-faq-list">
            {FAQS.map((faq, i) => (
              <div key={i} className="sv-faq-item">
                <button className="sv-faq-q" onClick={() => toggleFaq(i)}>
                  <span>{faq.q}</span>
                  <span>{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && <div className="sv-faq-a">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA / CONSULTATION FORM ───────────── */}
      <section className="sv-section" id="consultation">
        <div className="container">
          <div className="sv-cta-box">
            <div>
              <span className="sv-eyebrow" style={{ color: C.goldLight }}>{t("scCtaEyebrow")}</span>
              <h2 className="sv-serif" style={{ fontSize: "36px", marginBottom: "16px" }}>{t("scCtaTitle")}</h2>
              <p style={{ color: "rgba(255,255,255,0.8)", lineHeight: "1.7", marginBottom: "24px" }}>
                {t("scCtaSubtitle")}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", fontSize: "14px" }}>
                <div>📍 {t("scCtaLocation")}</div>
                <div>📞 +968 2400 0000</div>
                <div>✉️ visa@yoursite.com</div>
              </div>
            </div>
            <form className="sv-form" onSubmit={(e) => e.preventDefault()}>
              <h4 style={{ fontSize: "18px", fontWeight: "700", marginBottom: "8px" }}>{t("scFormTitle")}</h4>
              <input type="text" className="sv-input" placeholder={t("scFormName")} required />
              <input type="email" className="sv-input" placeholder={t("scFormEmail")} required />
              <input type="tel" className="sv-input" placeholder={t("scFormPhone")} required />
              <select className="sv-input" required defaultValue="">
                <option value="" disabled>{t("scFormSelectDest")}</option>
                {COUNTRIES.map((c, i) => (
                  <option key={i} value={c.name}>{c.name}</option>
                ))}
              </select>
              <button type="submit" className="sv-btn-primary" style={{ width: "100%", marginTop: "8px" }}>
                {t("scFormSubmitBtn")}
              </button>
            </form>
          </div>
        </div>
      </section>

      <DefaultFooter />
    </div>
    </>
  );
};

export default SchengenVisa;